<script>
  import CausalView from "./CausalView.js";
  import JugglerEdit from "./JugglerEdit.svelte";
  import EventEdit from "./EventEdit.svelte";
  import Rhythm from "./logical_pattern/Rhythm.js";
  import { onMount } from "svelte";
  import { tick } from 'svelte';

  export let initialJif = null;
  export let initialNumBeats = 6;
  export let initialNumJugglers = 2;
  export let initialSiteswap = '';
  export let onSimulate = () => {}; // Callback for simulation

  let causalView = null;
  let history = [];
  let historyPosition = 0;
  let beats = initialNumBeats;
  let jugglers = initialNumJugglers;
  let siteswap = initialSiteswap;
  let isFromHistoryChange = false;
  let jugglerEditPosition = { x: 0, y: 0 };
  let eventEditPosition = { x: 0, y: 0 };
  let editingJuggler = null;
  let editingEvent = null;
  let jugglerNames = [];

  $: canUndo = historyPosition > 0;
  $: canRedo = historyPosition + 1 < history.length;
  $: canRemoveBeat = beats > 1;
  $: canRemoveJuggler = jugglers > 1;

  onMount(() => {
    causalView = new CausalView('causalViewContainer');
    causalView.setNumBeats(beats);
    causalView.setNumJugglers(jugglers);
    if (initialJif) fromJIF(initialJif);
    if (initialSiteswap) fromSiteswap(initialSiteswap);
    history = [toJIF()];

    causalView.onChange(() => {
      if (!isFromHistoryChange) {
        recordHistory();
      } else {
        isFromHistoryChange = false;
      }
      beats = causalView.pattern.numBeats;
      jugglers = causalView.pattern.numJugglers;
      console.log(toSiteswap());
    });

    causalView.onJugglerDoubleClick(juggler => {
      closeDialogs()
      editingJuggler = juggler;
      jugglerEditPosition = { x: 100, y: 100 };
    });

    causalView.onEventDoubleClick(event => {
      closeDialogs()
      jugglerNames = causalView.pattern.jugglers.map(juggler => juggler.nameOrDefault())
      editingEvent = event;
      eventEditPosition = { x: 100, y: 100 };
    });
  });

  function handleKeydown(e) {
    if (e.key === 'y' && e.ctrlKey) redo();
    else if (e.key === 'z' && e.ctrlKey) undo();
  }

  function undo() {
    if (canUndo) {
      historyPosition--;
      isFromHistoryChange = true;
      fromJIF(history[historyPosition]);
    }
  }

  function redo() {
    if (canRedo) {
      historyPosition++;
      isFromHistoryChange = true;
      fromJIF(history[historyPosition]);
    }
  }

  function recordHistory() {
    const jif = toJIF();
    if (historyPosition + 1 < history.length) {
      history.length = historyPosition + 1;
    }
    history.push(jif);
    historyPosition++;
  }

  function handleSimulateClick() {
    if (causalView.pattern.valid) onSimulate(toJIF());
  }

  function handleJugglerEditSave(event) {
    causalView.updateJuggler(editingJuggler, event.detail);
    closeDialogs()
  }

  function handleEventDelete() {
    causalView.removeEvent(editingEvent);
    closeDialogs()
  }

  function handleEventEditSave(event) {
    causalView.updateEvent(editingEvent, event.detail);
    closeDialogs();
  }

  function closeDialogs() {
    editingJuggler = null;
    editingEvent = null;
  }

  function fromJIF(jif) { causalView.fromJIF(jif); }
  function toJIF() { return causalView.toJIF(); }
  function fromSiteswap(siteswap) { causalView.fromSiteswap(siteswap); }
  function toSiteswap() { return causalView.siteswap; }
  function addBeat() { causalView.addBeat(); }
  function removeBeat() { causalView.removeBeat(); }
  function setNumBeats(numBeats) { causalView.setNumBeats(numBeats); }
  function addJuggler() { causalView.addJuggler(); }
  function removeJuggler() { causalView.removeJuggler(); }
  function setNumJugglers(numJugglers) { causalView.setNumJugglers(numJugglers); }
  function renameJuggler(jugglerIndex, newName) { causalView.renameJuggler(jugglerIndex, newName); }
  function setJugglerDelay(jugglerIndex, delay) { causalView.setJugglerDelay(jugglerIndex, delay); }
  function setJugglerRhythm(jugglerIndex, rhythm) { causalView.setJugglerRhythm(jugglerIndex, rhythm); }
</script>

{#if editingJuggler !== null}
  <JugglerEdit
    juggler={editingJuggler}
    position={jugglerEditPosition}
    on:save={handleJugglerEditSave}
    on:cancel={closeDialogs} />
{/if}

{#if editingEvent !== null}
  <EventEdit
    event={editingEvent}
    position={eventEditPosition}
    bind:jugglerNames={jugglerNames}
    on:save={handleEventEditSave}
    on:delete={handleEventDelete}
    on:cancel={closeDialogs} />
{/if}

<svelte:window on:keydown={handleKeydown}/>
<div id="editorContainer">
  <div id="controls">
    <span class="number-input-group">
      <button disabled={!canRemoveBeat} class="left" on:click={removeBeat}>-</button>
      <span class="material-icons icon" title="Number of beats">view_week</span>
      <input type="number" id="beats" min="1" bind:value={beats} on:change={() => setNumBeats(beats) } />
      <button class="enable right" on:click={addBeat}>+
      </button>
    </span>
    <span class="number-input-group">
      <button disabled={!canRemoveJuggler} class="left" on:click={removeJuggler}>-</button>
      <span class="material-icons icon" title="Number of jugglers">people</span>
      <input type="number" id="jugglers" min="1" bind:value={jugglers} on:change={() => setNumJugglers(jugglers) } />
      <button class="enable right" on:click={addJuggler}>+
      </button>
    </span>
    <div class="input-group">
      <span class="material-icons icon left" title="Siteswap">pin</span>
      <input type="text" id="siteswap" class="right" bind:value={siteswap} on:change={() => fromSiteswap(siteswap) } />
    </div>
    <div class="input-group">
        <button id="simulate" disabled={!causalView?.pattern.valid} class="rounded material-icons" on:click={handleSimulateClick}>play_arrow</button>
    </div>
    <div class="input-group">
        <button id="undo" disabled={!canUndo} class="material-icons left" on:click={undo}>undo</button>
        <button id="redo" disabled={!canRedo} class="material-icons right" on:click={redo}>redo</button>
    </div>
  </div>
  <div id="causalViewContainer"></div>
</div>

<style>
  #editorContainer {
    width: 100%;
    height: 100%;
  }

  #causalViewContainer {
    width: 100%;
    height: 100%;
  }

  #controls {
    padding: 10px;
    border-radius: 5px;
    padding-top: 20px;
    display: flex;
    justify-content: space-evenly;
    align-content: center;
  }

  .input-group, .number-input-group {
    display: flex;
    justify-content: center;
  }

  .number-input-group input {
    width: 60px;
    text-align: center;
    border: 1px solid #ddd;
    border-width: 1px 0;
    font-size: 36px;
    vertical-align: middle;
    text-align: center;
  }

  .number-input-group button, .input-group button {
    width: 50px;
    height: 53px;
    background: #f2f2f2;
    background-color: #eee;
    font-size: 36px;
    padding: 8px 5px 8px 5px;
    border: 1px solid #ddd;
    vertical-align: middle;
    text-align: center;
    color: black;
    cursor: pointer;
  }

  .number-input-group button:disabled, .input-group button:disabled {
    color: grey;
    background-color: #ddd;
    cursor: default;
  }
  .left {
    border-radius: 10px 0 0 10px;
  }
  .right {
    border-radius: 0 10px 10px 0;
  }
  .number-input-group input {
    height: 49px;
  }
  .input-group input {
    height: 49px;
    width: 400px;
  }
  .enable {
    color: black;
    cursor: pointer;
  }
.icon {
    height: 51px;
    font-size: 46px;
    border: 1px solid #ddd;
    border-width: 1px 0;
    vertical-align: middle;
    text-align: center;
    padding: 0 5px;
}
.rounded {
    border-radius: 10px;
}
</style>
